import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Grow,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import apiService from '../services/api';
import { WordPressProject } from '../types';

interface StatCard {
  title: string;
  value: number;
  color: string;
}

const getConstituencyForWard = (wardRaw?: string): string => {
  if (!wardRaw) return 'County wide';
  const w = wardRaw.toLowerCase();

  if (
    w.includes('port reitz') || w.includes('portreiz') || w.includes('portriz') ||
    w.includes('kipevu') ||
    w.includes('airport') ||
    w.includes('changamwe') ||
    w.includes('chaani')
  ) {
    return 'Changamwe';
  }

  if (
    w.includes('jomvu kuu') || w.includes('jomvu ward') || w === 'jomvu' ||
    w.includes('miritini') ||
    w.includes('mikindani')
  ) {
    return 'Jomvu';
  }

  if (
    w.includes('mjambere') ||
    w.includes('junda') ||
    w.includes('bamburi') ||
    w.includes('mwakirunge') ||
    w.includes('mtopanga') ||
    w.includes('magogoni') ||
    w.includes('shanzu')
  ) {
    return 'Kisauni';
  }

  if (
    w.includes('frere town') || w.includes('freretown') ||
    w.includes("ziwa la ng'ombe") || w.includes('ziwa la ngom') ||
    w.includes('mkomani') ||
    w.includes('kongowea') ||
    w.includes('kadzandani')
  ) {
    return 'Nyali';
  }

  if (
    w.includes('mtongwe') ||
    w.includes('shika adabu') ||
    w.includes('bofu') ||
    w.includes('likoni') ||
    w.includes('timbwani')
  ) {
    return 'Likoni';
  }

  if (
    w.includes('tudor') ||
    w.includes('mji wa kale') || w.includes('makadara') || w.includes('old town') ||
    w.includes('tononoka') ||
    w.includes('shimanzi') || w.includes('ganjoni') ||
    w.includes('majengo') ||
    w.includes('mvita')
  ) {
    return 'Mvita';
  }

  return 'County wide';
};

const normalizeProjectStatus = (rawStatus?: string): 'planned' | 'ongoing' | 'completed' | 'delayed' => {
  const value = (rawStatus || '').trim().toLowerCase();

  if (!value || value === 'nil') {
    return 'planned';
  }

  if (
    value.includes('not started') ||
    value.includes('has not taken off') ||
    value.includes('awaiting') ||
    value.includes('not been commissioned') ||
    value.includes('at inception')
  ) {
    return 'planned';
  }

  if (value.includes('delay')) {
    return 'delayed';
  }

  const percentageMatch = value.match(/(\d+(?:\.\d+)?)\s*%/);
  if (percentageMatch) {
    const pct = parseFloat(percentageMatch[1]);
    if (!Number.isNaN(pct)) {
      if (pct >= 100) return 'completed';
      if (pct > 0) return 'ongoing';
      return 'planned';
    }
  }

  if (value.includes('complete')) {
    return 'completed';
  }

  if (value.includes('ongoing') || value.includes('progress')) {
    return 'ongoing';
  }

  return 'ongoing';
};

const STATUS_COLORS = ['#ff9800', '#4caf50', '#f44336', '#9e9e9e'];

const CitizenDashboardPage: React.FC = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState<StatCard[]>([]);
  const [constituencyChartData, setConstituencyChartData] = useState<any[]>([]);
  const [statusChartData, setStatusChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const handleViewProjects = () => {
    navigate('/wordpress-projects');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');

      try {
        const [projectsResponse, statsResponse] = await Promise.all([
          apiService.get('/wordpress-projects', { params: { limit: 1000 } }),
          apiService.get('/wordpress-projects/statistics'),
        ]);

        let projects: WordPressProject[] = [];
        let wpStats: any = null;

        if (projectsResponse?.success) {
          projects = projectsResponse.data || [];
        }

        if (statsResponse?.success) {
          wpStats = statsResponse.data || {};
        }

        const constituencyCounts: Record<string, number> = {};
        const statusCounts: Record<string, number> = {
          planned: 0,
          ongoing: 0,
          completed: 0,
          delayed: 0,
        };
        const wardCounts: Record<string, number> = {};

        projects.forEach((project: WordPressProject) => {
          const constituency = getConstituencyForWard(project.ward);
          constituencyCounts[constituency] = (constituencyCounts[constituency] || 0) + 1;

          const statusKey = normalizeProjectStatus(project.status);
          statusCounts[statusKey] += 1;

          const wardName = project.ward || 'Unspecified';
          wardCounts[wardName] = (wardCounts[wardName] || 0) + 1;
        });

        const constituencyData = Object.entries(constituencyCounts).map(([name, count]) => ({
          name,
          projects: count,
        }));

        const statusData = Object.entries(statusCounts).map(([key, value]) => ({
          name: key.charAt(0).toUpperCase() + key.slice(1),
          value,
        }));

        setConstituencyChartData(constituencyData);
        setStatusChartData(statusData);

        const totalProjects = wpStats?.totalProjects ?? projects.length;
        const totalComments = wpStats?.totalComments ?? 0;
        const completedProjects = statusCounts.completed;
        const ongoingProjects = statusCounts.ongoing;

        setStats([
          { title: 'Total Public Projects', value: totalProjects, color: '#1976d2' },
          { title: 'Completed Projects', value: completedProjects, color: '#4caf50' },
          { title: 'Ongoing Projects', value: ongoingProjects, color: '#ff9800' },
          { title: 'Total Feedback Entries', value: totalComments, color: '#003c8f' },
        ]);
      } catch (err: any) {
        console.error('Failed to load citizen dashboard data:', err);
        setError(err.response?.data?.message || 'Failed to load public dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              component="img"
              src="/logo192.png"
              alt="Mombasa County Logo"
              sx={{ width: 56, height: 56, borderRadius: '50%' }}
            />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Mombasa County
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Public Project Dashboard
              </Typography>
            </Box>
          </Box>

          <Button variant="outlined" color="primary" onClick={handleLogin}>
            Staff Login
          </Button>
        </Box>

        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            textAlign: 'center',
            mb: 4,
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            County Projects at a Glance
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'text.secondary' }}>
            Citizens can track county projects, view progress and share feedback.
          </Typography>

          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
            Explore ongoing, planned and completed development projects across all constituencies
            in Mombasa County. Click below to view the full list of projects.
          </Typography>

          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleViewProjects}
          >
            View County Projects
          </Button>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Grid container justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Grid>
        ) : (
          <>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {stats.map((stat, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card
                    sx={{
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      borderLeft: `4px solid ${stat.color}`,
                    }}
                  >
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        {stat.title}
                      </Typography>
                      <Typography variant="h5" sx={{ color: stat.color, fontWeight: 'bold' }}>
                        {stat.value}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Projects by Constituency
                  </Typography>
                  {constituencyChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={constituencyChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" interval={0} angle={-30} textAnchor="end" height={80} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="projects" fill="#8884d8" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 4, textAlign: 'center' }}
                    >
                      No data available
                    </Typography>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Public Project Status Distribution
                  </Typography>
                  {statusChartData.length > 0 && statusChartData.some((s) => s.value > 0) ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={statusChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value }) => `${name}: ${value}`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {statusChartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={STATUS_COLORS[index % STATUS_COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 4, textAlign: 'center' }}
                    >
                      No status data available
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Highlights from Around the County
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Grow in timeout={700}>
                  <Paper
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: '0 10px 25px rgba(15, 23, 42, 0.15)',
                    }}
                  >
                    <Box
                      component="img"
                      src="/images/mombasa-ferry.png"
                      alt="Aerial view of Likoni ferry crossing"
                      sx={{
                        width: '100%',
                        borderRadius: 2,
                        objectFit: 'cover',
                        transition: 'transform 0.6s ease, filter 0.6s ease',
                        '&:hover': {
                          transform: 'scale(1.04)',
                          filter: 'brightness(1.03)',
                        },
                      }}
                    />
                  </Paper>
                </Grow>
              </Grid>

              <Grid item xs={12} md={6}>
                <Grow in timeout={850}>
                  <Paper
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: '0 10px 25px rgba(15, 23, 42, 0.15)',
                    }}
                  >
                    <Box
                      component="img"
                      src="/images/mombasa-tusks.png"
                      alt="Iconic Mombasa tusks landmark"
                      sx={{
                        width: '100%',
                        borderRadius: 2,
                        objectFit: 'cover',
                        transition: 'transform 0.6s ease, filter 0.6s ease',
                        '&:hover': {
                          transform: 'scale(1.04)',
                          filter: 'brightness(1.03)',
                        },
                      }}
                    />
                  </Paper>
                </Grow>
              </Grid>

              <Grid item xs={12} md={4}>
                <Grow in timeout={1000}>
                  <Paper
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: '0 10px 25px rgba(15, 23, 42, 0.15)',
                    }}
                  >
                    <Box
                      component="img"
                      src="/images/mombasa-bridge.png"
                      alt="Bridge connecting parts of Mombasa"
                      sx={{
                        width: '100%',
                        borderRadius: 2,
                        objectFit: 'cover',
                        transition: 'transform 0.6s ease, filter 0.6s ease',
                        '&:hover': {
                          transform: 'scale(1.04)',
                          filter: 'brightness(1.03)',
                        },
                      }}
                    />
                  </Paper>
                </Grow>
              </Grid>

              <Grid item xs={12} md={4}>
                <Grow in timeout={1150}>
                  <Paper
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: '0 10px 25px rgba(15, 23, 42, 0.15)',
                    }}
                  >
                    <Box
                      component="img"
                      src="/images/mombasa-leadership.jpg"
                      alt="County leadership at work"
                      sx={{
                        width: '100%',
                        borderRadius: 2,
                        objectFit: 'cover',
                        transition: 'transform 0.6s ease, filter 0.6s ease',
                        '&:hover': {
                          transform: 'scale(1.04)',
                          filter: 'brightness(1.03)',
                        },
                      }}
                    />
                  </Paper>
                </Grow>
              </Grid>

              <Grid item xs={12} md={4}>
                <Grow in timeout={1300}>
                  <Paper
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: '0 10px 25px rgba(15, 23, 42, 0.15)',
                    }}
                  >
                    <Box
                      component="img"
                      src="/images/mombasa-community.png"
                      alt="Community engagement activity"
                      sx={{
                        width: '100%',
                        borderRadius: 2,
                        objectFit: 'cover',
                        transition: 'transform 0.6s ease, filter 0.6s ease',
                        '&:hover': {
                          transform: 'scale(1.04)',
                          filter: 'brightness(1.03)',
                        },
                      }}
                    />
                  </Paper>
                </Grow>
              </Grid>

              <Grid item xs={12} md={4}>
                <Grow in timeout={1450}>
                  <Paper
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: '0 10px 25px rgba(15, 23, 42, 0.15)',
                    }}
                  >
                    <Box
                      component="img"
                      src="/images/mombasa-cleanup.jpg"
                      alt="County environmental cleanup activity"
                      sx={{
                        width: '100%',
                        borderRadius: 2,
                        objectFit: 'cover',
                        transition: 'transform 0.6s ease, filter 0.6s ease',
                        '&:hover': {
                          transform: 'scale(1.04)',
                          filter: 'brightness(1.03)',
                        },
                      }}
                    />
                  </Paper>
                </Grow>
              </Grid>

              <Grid item xs={12} md={4}>
                <Grow in timeout={1600}>
                  <Paper
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: '0 10px 25px rgba(15, 23, 42, 0.15)',
                    }}
                  >
                    <Box
                      component="img"
                      src="/images/mombasa-trucks.jpg"
                      alt="County garbage collection trucks"
                      sx={{
                        width: '100%',
                        borderRadius: 2,
                        objectFit: 'cover',
                        transition: 'transform 0.6s ease, filter 0.6s ease',
                        '&:hover': {
                          transform: 'scale(1.04)',
                          filter: 'brightness(1.03)',
                        },
                      }}
                    />
                  </Paper>
                </Grow>
              </Grid>

              <Grid item xs={12} md={4}>
                <Grow in timeout={1750}>
                  <Paper
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: '0 10px 25px rgba(15, 23, 42, 0.15)',
                    }}
                  >
                    <Box
                      component="img"
                      src="/images/mombasa-school.jpg"
                      alt="Learners in a classroom"
                      sx={{
                        width: '100%',
                        borderRadius: 2,
                        objectFit: 'cover',
                        transition: 'transform 0.6s ease, filter 0.6s ease',
                        '&:hover': {
                          transform: 'scale(1.04)',
                          filter: 'brightness(1.03)',
                        },
                      }}
                    />
                  </Paper>
                </Grow>
              </Grid>
            </Grid>
          </>
        )}
      </Container>
    </Box>
  );
};

export default CitizenDashboardPage;
